// The guard look: the round kill and the cooldown marks (R2080,
// `ui-map-interface.md` §6.3.6, mockup round 099 card 04 with the snap of
// card 01 in front).
//
// A GUARD is any object that takes one hostile round out of the water on a
// cooldown (numbers.md §5): a set Sentinel with the Refit node, an arcade
// Reef Breakwater, and any later host. The look knows no host type. It
// reads a guard record (a point, a reach, the share of the clock left, a
// state, an ink and the radius of the host mark) and a kill record (the
// killer's point, the round's point, heading and ink, an age), the
// contract of the round's `look` interface, and nothing else.
//
// **The kill**, which every client that sees the kill draws: the pale snap
// of card 01 runs from the guard to the round first, then the four corner
// brackets of card 04 snap shut on the round in the guard's ink while the
// round fades, the white flash disc of card 04 stands at the moment the
// snap reaches the round, and the small white cross the close leaves fades
// out. Every time is a WALL second: a look is presentation and must not
// stretch with the map clock.
//
// **The cooldown marks**, which only a guard whose clock reaches the client
// draws (an own guard, a team guard, a public structure): the pie pip
// with the whole seconds left in it, the brackets that track the round the guard takes
// next while it is ready, and the dashed brackets in the warning ink on
// every hostile round inside the reach while the clock runs (blocked). A
// hostile guard has no clock on this client, and the layer draws its kill
// alone.
//
// This is a leaf (`leaves.test.ts`). It names the sinks through a type-only
// clause; the guard layer `fast/layers/guard.ts` owns the batches, the clock
// and the lists, and hands them in. Every figure is a named figure of
// `[presentation.guard]` (`GUARD`), and the digit is the one of the clock
// face of the tube reload (`reload.toml clock-digit`, R2418). Nothing here
// allocates.

import { GUARD, PALETTE_BAD, RELOAD_LIMITS, RELOAD_LOOKS } from '../gen/presentation';
import type { CircleSink, SegmentSink } from '../fast/layers/batches';
import { out } from './combat-recipe-kit';
import { CORE_INK, DEEP_INK, PALE_INK } from './recipe-kit';
import { clockDigits } from './reload-recipes';
import { packHex } from './color';
import { TAU, clamp01 } from './math';

/** The white of the flash and of the cross: the core ink of the kits,
 *  white in the dark identity and the `core` role on paper (R3293). */
const WHITE = CORE_INK;
/** The one warning ink of the card kit (`--bad`): a round seen, not taken. */
const WARN_INK = packHex(PALETTE_BAD);
/** The clock face of the tube reload, whose digit the pip prints (R2418). */
const CLOCK = RELOAD_LOOKS['clock-digit'].fig;

/** What a guard is doing (the round's contract). */
export type GuardPhase = 'ready' | 'cooling' | 'blocked' | 'idle';

/**
 * The state of a guard from its clock and the hostile rounds round it:
 * cooling while the clock runs and no hostile round is in reach, blocked
 * while the clock runs and one is (it passes), ready while the clock is
 * done and one is inside `watch_reaches` reaches (it is coming), idle while
 * the clock is done and nothing is near.
 */
export function guardPhase(frac: number, inReach: number, watched: boolean): GuardPhase {
  if (frac > 0) return inReach > 0 ? 'blocked' : 'cooling';
  return watched ? 'ready' : 'idle';
}

/** The sinks and the zoom one pass draws with. */
export interface GuardSinks {
  segs: SegmentSink;
  circles: CircleSink;
  /** The camera zoom, so a stroke keeps `min_screen_px` on the screen. */
  zoom: number;
  /** The segment flag of a round cap (`F_ROUND`), which the layer hands
   *  in, because a leaf takes no value from the engine. */
  round: number;
  /** Writes a mono run centred on a point, with a halo, in world px. */
  text: (s: string, x: number, y: number, px: number, ink: number, alpha: number) => void;
}

/** A round as a mark of the look reads it. */
export interface GuardRound {
  x: number;
  y: number;
}

/** One guard as the cooldown marks read it. */
export interface GuardMarks {
  x: number;
  y: number;
  /** The centre of the pip and its radius, world px. */
  px: number;
  py: number;
  r: number;
  reach: number;
  /** The share of the clock left, 0 (ready) to 1. */
  frac: number;
  /** The whole world seconds left, the figure the card prints. */
  left: number;
  phase: GuardPhase;
  ink: number;
  /** The round the guard takes next, while it is ready, and its distance. */
  next: GuardRound | null;
  nextD: number;
  /** The hostile rounds inside the reach, while it is blocked. */
  near: readonly GuardRound[];
}

/** One kill as the kill draw reads it. */
export interface GuardKill {
  /** The killer's point: the guard, or a burst's detonation. */
  fromX: number;
  fromY: number;
  /** The round's point and heading at the kill. */
  x: number;
  y: number;
  heading: number;
  /** The guard's ink, and the round's own. */
  ink: number;
  roundInk: number;
  /** Wall seconds since the kill. */
  age: number;
}

/** The whole life of one kill: the snap, the close and the cross. */
export const KILL_LIFE_SEC = GUARD.snapSec + GUARD.closeSec + GUARD.crossSec;

/** A stroke of `px` world px, never under the screen floor. */
function stroke(s: GuardSinks, px: number): number {
  return Math.max(px, GUARD.minScreenPx / Math.max(s.zoom, 1e-6));
}

/** Four corner brackets of half-size `half` round a point (card 04). */
function brackets(
  s: GuardSinks, x: number, y: number, half: number, ink: number, alpha: number, dash = 0,
): void {
  if (alpha <= 0 || half <= 0) return;
  const leg = Math.max(GUARD.legMinPx, half * GUARD.legShare);
  const w = stroke(s, GUARD.strokePx);
  for (let qx = -1; qx <= 1; qx += 2) {
    for (let qy = -1; qy <= 1; qy += 2) {
      const cx = x + qx * half, cy = y + qy * half;
      s.segs.line(cx, y + qy * (half - leg), cx, cy, w, ink, alpha, 0, dash, dash);
      s.segs.line(cx, cy, x + qx * (half - leg), cy, w, ink, alpha, 0, dash, dash);
    }
  }
}

/** The round as it was at the kill: the card's dart and its white core. */
function ghost(s: GuardSinks, k: GuardKill, alpha: number): void {
  if (alpha <= 0) return;
  const c = Math.cos(k.heading), n = Math.sin(k.heading);
  const nose = GUARD.ghostNosePx, tail = GUARD.ghostTailPx, wing = GUARD.ghostWingPx;
  const nx = k.x + nose * c, ny = k.y + nose * n;
  // The two wing tips: (-tail, ±wing) turned by the heading.
  const bx = k.x - tail * c, by = k.y - tail * n;
  const w = stroke(s, GUARD.strokePx);
  const a = GUARD.ghostAlpha * alpha;
  s.segs.line(nx, ny, bx - wing * n, by + wing * c, w, k.roundInk, a, s.round);
  s.segs.line(nx, ny, bx + wing * n, by - wing * c, w, k.roundInk, a, s.round);
  s.circles.disc(k.x, k.y, GUARD.ghostCorePx, WHITE, 0.9 * alpha);
}

/**
 * One kill at its age. The snap comes first and the bracket close follows
 * it; the flash stands where the snap reaches the round; the cross fades
 * after the close. Past `KILL_LIFE_SEC` it draws nothing.
 */
export function packKill(s: GuardSinks, k: GuardKill): void {
  const t = k.age;
  if (t < 0 || t >= KILL_LIFE_SEC) return;
  // The snap of card 01: pale, 1 px, falling to 0 over its span.
  if (t < GUARD.snapSec) {
    const a = GUARD.snapAlpha * (1 - t / GUARD.snapSec);
    s.segs.line(k.fromX, k.fromY, k.x, k.y, stroke(s, GUARD.snapWidthPx), PALE_INK, a, s.round);
    ghost(s, k, 1);
    return;
  }
  const tc = t - GUARD.snapSec;
  // The flash disc of card 04, at the moment the snap reaches the round.
  if (tc < GUARD.flashSec) s.circles.disc(k.x, k.y, GUARD.flashPx, WHITE, GUARD.flashAlpha);
  if (tc < GUARD.closeSec) {
    const p = tc / GUARD.closeSec;
    const half = GUARD.closeFromPx - (GUARD.closeFromPx - GUARD.closeToPx) * out(p);
    brackets(s, k.x, k.y, half, k.ink, GUARD.closeAlpha);
    ghost(s, k, 1 - p);
    return;
  }
  // The small white cross the close leaves.
  const a = GUARD.crossAlpha * (1 - (tc - GUARD.closeSec) / GUARD.crossSec);
  if (a <= 0) return;
  const r = GUARD.crossPx, w = stroke(s, GUARD.strokePx);
  s.segs.line(k.x - r, k.y - r, k.x + r, k.y + r, w, WHITE, a, s.round);
  s.segs.line(k.x - r, k.y + r, k.x + r, k.y - r, w, WHITE, a, s.round);
}

/**
 * The cooldown marks of one guard whose clock this client holds: the
 * tracking brackets while it is ready, the dashed warning brackets on every
 * hostile round in reach while it is blocked, and the pie pip at every
 * phase, with the whole seconds left in it while the clock runs.
 */
export function packCool(s: GuardSinks, g: GuardMarks): void {
  if (g.phase === 'ready' && g.next) {
    const q = clamp01((g.nextD - g.reach) / g.reach);
    brackets(s, g.next.x, g.next.y, GUARD.trackPx + GUARD.trackGrowPx * q, g.ink, GUARD.trackAlpha);
  } else if (g.phase === 'blocked') {
    for (const r of g.near) {
      brackets(s, r.x, r.y, GUARD.blockedPx, WARN_INK, GUARD.blockedAlpha, GUARD.blockedDashPx);
    }
  }
  const px = g.px, py = g.py, R = g.r;
  const c = s.circles;
  c.disc(px, py, R, DEEP_INK, GUARD.pipGroundAlpha);
  c.ring(px, py, R, stroke(s, 1), PALE_INK, GUARD.pipRimAlpha);
  if (g.frac <= 0) {
    c.disc(px, py, R, g.ink, g.phase === 'idle' ? GUARD.pipIdleAlpha : GUARD.pipReadyAlpha);
  } else {
    // The pie: an arc as wide as the radius, centred on half of it, fills
    // the wedge from the top, clockwise, as the clock runs out.
    const sweep = (1 - g.frac) * TAU;
    if (sweep > 0) c.arc(px, py, R / 2, R, -Math.PI / 2, sweep, g.ink, GUARD.pipFillAlpha);
    // The digit of the clock face, at its share of the face (R2418).
    if (g.left > 0) {
      const k = R / CLOCK.r_px;
      s.text(clockDigits(g.left), px, py + CLOCK.text_dy_px * k, CLOCK.text_px * k,
        RELOAD_LIMITS.flash, CLOCK.text_alpha);
    }
  }
}
